import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CampaignCodeUser,
  useCampaignCode,
  useCampaignCodeUsers,
} from "@/lib/api/campaign-codes";
import { requirePermission } from "@/lib/casl/route-guards";
import { Link, createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Loader2,
  MessageSquare,
} from "lucide-react";
import { memo, useCallback, useState } from "react";

export const Route = createFileRoute("/_authenticated/campaign-codes/$codeId")({
  component: CampaignCodeUsers,
  beforeLoad: ({ context }) => {
    requirePermission(context.user, "read", "campaign-codes");
  },
});

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const UserRow = memo(function UserRow({ user }: { user: CampaignCodeUser }) {
  return (
    <div
      className={`flex items-center justify-between px-4 py-3 ${user.deactivated ? "bg-muted/30" : ""}`}
    >
      <div className="flex items-center gap-3">
        <Avatar className={user.deactivated ? "opacity-50" : ""}>
          <AvatarFallback>
            {user.name
              ?.split(" ")
              .map((n) => n[0])
              .join("")
              .slice(0, 2)}
          </AvatarFallback>
        </Avatar>
        <div>
          <div className="flex items-center gap-2">
            <p
              className={`font-medium ${user.deactivated ? "text-muted-foreground" : ""}`}
            >
              {user.name}
            </p>
            <Badge
              variant={user.deactivated ? "secondary" : "default"}
              className={`text-xs ${!user.deactivated ? "bg-green-100 text-green-700 hover:bg-green-100" : ""}`}
            >
              {user.deactivated ? "Deactivated" : "Active"}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {user.email || user.phone}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right text-sm">
          <p className="text-muted-foreground">
            Joined {format(new Date(user.assigned_at), "MMM d, yyyy")}
          </p>
          <p className="text-xs text-muted-foreground">
            {user.total_messages_sent} messages · {user.days_active} days active
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user.sdk_url && user.mentorship_id && (
            <Link
              to="/mentorships/$mentorshipId"
              params={{ mentorshipId: user.mentorship_id }}
            >
              <Button variant="outline" size="sm" className="gap-1">
                <MessageSquare className="h-4 w-4" />
                Chat
              </Button>
            </Link>
          )}
          <Link to="/users/$userId" params={{ userId: user.user_id }}>
            <Button variant="outline" size="sm">
              View Profile
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
});

function CampaignCodeUsers() {
  const { codeId } = Route.useParams();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const { data: campaignCode, isLoading: isLoadingCode } =
    useCampaignCode(codeId);
  const { data: usersData, isLoading: isLoadingUsers } = useCampaignCodeUsers(
    codeId,
    page,
    limit,
  );

  const users = usersData?.users ?? [];
  const pagination = usersData?.pagination;

  const handleLimitChange = useCallback((value: string) => {
    setLimit(Number(value));
    setPage(1);
  }, []);

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link to="/campaign-codes">
          <Button variant="ghost" size="sm" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Campaign Codes
          </Button>
        </Link>
      </div>

      {isLoadingCode ? (
        <div className="mb-6 flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-8 w-32" />
        </div>
      ) : campaignCode ? (
        <>
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">
                <span className="font-mono">{campaignCode.code}</span>
              </h2>
              {campaignCode.description && (
                <p className="mt-1 text-muted-foreground">
                  {campaignCode.description}
                </p>
              )}
            </div>
            <div className="flex items-center gap-4">
              <Badge variant={campaignCode.enabled ? "default" : "secondary"}>
                {campaignCode.enabled ? "Active" : "Inactive"}
              </Badge>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Slots Used</p>
                <p className="text-lg font-semibold">
                  {campaignCode.redeemed_count ?? 0} / {campaignCode.slots}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border">
            <div className="border-b bg-muted/50 px-4 py-3">
              <h3 className="font-semibold">
                Users ({pagination?.total ?? users.length})
              </h3>
            </div>
            {isLoadingUsers ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : users.length > 0 ? (
              <div className="divide-y">
                {users.map((user) => (
                  <UserRow key={user.user_id} user={user} />
                ))}
              </div>
            ) : (
              <div className="py-12 text-center text-muted-foreground">
                No users have redeemed this code yet.
              </div>
            )}
            <div className="flex items-center justify-between border-t px-4 py-3">
              <div className="flex items-center gap-4">
                <p className="text-sm text-muted-foreground">
                  {pagination ? (
                    <>
                      Showing {users.length > 0 ? (page - 1) * limit + 1 : 0} to{" "}
                      {Math.min(page * limit, pagination.total)} of{" "}
                      {pagination.total} users
                    </>
                  ) : (
                    <>Showing {users.length} users</>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Per page:
                  </span>
                  <Select
                    value={String(limit)}
                    onValueChange={handleLimitChange}
                  >
                    <SelectTrigger className="h-8 w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <SelectItem key={size} value={String(size)}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page}
                  {pagination?.totalPages ? ` of ${pagination.totalPages}` : ""}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={
                    pagination
                      ? page >= pagination.totalPages
                      : users.length < limit
                  }
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="py-12 text-center text-muted-foreground">
          Campaign code not found.
        </div>
      )}
    </div>
  );
}
